/* eslint-disable no-async-promise-executor */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PF_SERVER_URL } from "../Constants";
import { AllProgressCheckStatuses, IUser, ProgramData, ProgressCheckStatus } from "@interfaces/Interfaces";
import RemoteDataController from "./RemoteDataController";

/**
 * Gets the status of all of the user's submitted progress checks from database
 * @param {IUser} user - The current user
 * @param {string} programID - The current Program Module ID
 * @returns All the progress check statuses and list of mentors
 */
export async function getAllProgressCheckStatuses(
  user: IUser,
  programID: string
): Promise<AllProgressCheckStatuses | any> {
  return new Promise(async (resolve, reject) => {
    try {
      const url = PF_SERVER_URL + "/portfolio/Mentor/getPCs";

      const response: Response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          token: `${user.token}`,
          contactId: user.userData.contactID,
          menteeContactId: user.userData.contactID,
          pMId: programID,
          pCId: "ALL",
        }),
      });

      if (response) {
        const json = await response.json();

        if (json.success) {
          const progressCheckStatus = json.body;

          resolve(progressCheckStatus);
        } else {
          reject(json.message || json.error);
        }
      } else {
        reject("The server didn't respond");
      }
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      reject("An error has occurred");
    }
  });
}

/**
 * Gets the status of a single progress check for a user
 * @param user - The current user
 * @param programID - The current Program Module ID
 * @param progressCheckID - The ID for the requested progress check
 * @returns The current progress check status
 */
export async function getProgressCheckStatus(
  user: IUser,
  programID: string,
  progressCheckID: string
): Promise<ProgressCheckStatus | any> {
  return new Promise(async (resolve, reject) => {
    try {
      const url = PF_SERVER_URL + "/portfolio/Mentor/getPCs";

      const response: Response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: user.token,
          contactId: user.userData.contactID,
          menteeContactId: user.userData.contactID,
          pMId: programID,
          pCId: progressCheckID,
        }),
      });

      if (response) {
        const json = await response.json();

        if (json.success) {
          const progressCheckStatus = json.body;

          resolve(progressCheckStatus);
        } else {
          reject(json.message || json.error);
        }
      } else {
        reject("The server didn't respond");
      }
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      reject("An error has occurred");
    }
  });
}

/**
 * Creates an In-Progress progress check submission
 * @param user - The current user
 * @param programID - The current Program Module ID
 * @param progressCheckID - The ID for the requested progress check
 * @param operation - optional param, default value is "In-Progress"
 * @returns true if the request completed successfully
 */
export async function createProgressCheck(
  user: IUser,
  programID: string,
  progressCheckID: string,
  operation: string, // e.g. "In-Progress",
  cycle?: number
): Promise<boolean | any> {
  return new Promise(async (resolve, reject) => {
    try {
      const url = PF_SERVER_URL + "/portfolio/Mentor/updateSubmissionStatus";

      const response: Response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          token: `${user.token}`,
          contactId: user.userData.contactID,
          menteeContactId: user.userData.contactID,
          pMId: programID,
          pCId: progressCheckID,
          operation: operation,
          role: "Mentee",
          ...(cycle && { cycle: cycle.toString(10) }),
        }),
      });

      if (response) {
        const json = await response.json();

        if (json.success) {
          resolve(true);
        } else {
          reject(json.message || json.error);
        }
      } else {
        reject("The server didn't respond");
      }
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      reject("An error has occurred");
    }
  });
}

/**
 * Marks a progress check submission as completed
 * @param user - The current user
 * @param programID - The current Program Module ID
 * @param progressCheckID - The ID for the requested progress check
 * @returns true if the request completed successfully
 */
export async function completeProgressCheck(
  user: IUser,
  programID: string,
  progressCheckID: string,
  cycle?: number
): Promise<boolean | any> {
  return new Promise(async (resolve, reject) => {
    try {
      const url = PF_SERVER_URL + "/portfolio/Mentor/updateSubmissionStatus";

      const response: Response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          token: `${user.token}`,
          contactId: user.userData.contactID,
          menteeContactId: user.userData.contactID,
          pMId: programID,
          pCId: progressCheckID,
          operation: "Completed",
          role: "Mentee",
          ...(cycle && { cycle: cycle.toString(10) }),
        }),
      });

      if (response) {
        const json = await response.json();

        if (json.success) {
          resolve(true);
        } else {
          reject(json.message || json.error);
        }
      } else {
        reject("The server didn't respond");
      }
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      reject("An error has occurred");
    }
  });
}

/**
 * Updates the current progress check submission for approval by the Mentors/ OO
 * @param user - The current user
 * @param programID - The current Program Module ID
 * @param progressCheckID - The ID for the requested progress check
 * @returns true if the request completed successfully
 */
export async function updateProgressCheck(
  user: IUser,
  programID: string,
  progressCheckID: string,
  cycle?: number
): Promise<boolean | any> {
  return new Promise(async (resolve, reject) => {
    try {
      const url = PF_SERVER_URL + "/portfolio/Mentor/updateSubmissionStatus";

      const response: Response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          token: `${user.token}`,
          contactId: user.userData.contactID,
          menteeContactId: user.userData.contactID,
          pMId: programID,
          pCId: progressCheckID,
          operation: "Submitted",
          role: "Mentee",
          ...(cycle && { cycle: cycle.toString(10) }),
        }),
      });

      if (response) {
        const json = await response.json();

        if (json.success) {
          resolve(true);
        } else {
          reject(json.message || json.error);
        }
      } else {
        reject("The server didn't respond");
      }
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      reject("An error has occurred");
    }
  });
}

/**
 * Adds a comment to the current progress check
 * @param user - The current user
 * @param programID - The current Program Module ID
 * @param progressCheckID - The ID for the requested progress check
 * @param comment - The comment to add
 * @returns true if the request completed successfully
 */
export async function addComment(
  user: IUser,
  programID: string,
  progressCheckID: string,
  comment: string
): Promise<boolean | any> {
  return new Promise(async (resolve, reject) => {
    try {
      const url = PF_SERVER_URL + "/portfolio/Mentor/addComment";

      const response: Response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          token: `${user.token}`,
          contactId: user.userData.contactID,
          menteeContactId: user.userData.contactID,
          pMId: programID,
          pCId: progressCheckID,
          comment: comment,
          role: "Mentee",
        }),
      });

      if (response) {
        const json = await response.json();

        if (json.success) {
          resolve(true);
        } else {
          reject(json.message || json.error);
        }
      } else {
        reject("The server didn't respond");
      }
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      reject("An error has occurred");
    }
  });
}

export async function editComment(
  user: IUser,
  programID: string,
  progressCheckID: string,
  newComment: string,
  commentTimestamp: string
): Promise<boolean | any> {
  return new Promise(async (resolve, reject) => {
    try {
      const url = PF_SERVER_URL + "/portfolio/Mentor/editComment";

      const response: Response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          token: `${user.token}`,
          contactId: user.userData.contactID,
          menteeContactId: user.userData.contactID,
          pMId: programID,
          pCId: progressCheckID,
          newComment: newComment,
          commentTimestamp: commentTimestamp,
          role: "Mentee",
        }),
      });

      if (response) {
        const json = await response.json();

        if (json.success) {
          resolve(true);
        } else {
          reject(json.message || json.error);
        }
      } else {
        reject("The server didn't respond");
      }
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      reject("An error has occurred");
    }
  });
}

/**
 * Updates the progress check object in the database with the timestamp the user last read the comments
 * @param user - The current user
 * @param programID - The current Program Module ID
 * @param progressCheckID - The ID for the requested progress check
 * @returns true if the request completed successfully
 */
export async function markCommentsAsRead(
  user: IUser,
  programID: string,
  progressCheckID: string
): Promise<boolean | any> {
  return new Promise(async (resolve, reject) => {
    try {
      const url = PF_SERVER_URL + "/portfolio/Mentor/markCommentsAsRead";

      const response: Response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          token: `${user.token}`,
          contactId: user.userData.contactID,
          menteeContactId: user.userData.contactID,
          pMId: programID,
          pCId: progressCheckID,
          timestamp: new Date().toISOString(),
        }),
      });

      if (response) {
        const json = await response.json();

        if (json.success) {
          resolve(true);
        } else {
          reject(json.message || json.error);
        }
      } else {
        reject("The server didn't respond");
      }
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      reject("An error has occurred");
    }
  });
}

export async function markProgressCheckAsInProgress(
  user: IUser,
  program: ProgramData | null,
  progressCheckID: string
): Promise<boolean | any> {
  return new Promise(async (resolve, reject) => {
    try {
      if (program?.ProgressChecks && progressCheckID) {
        const progressCheck = program.ProgressChecks.find((progressCheck) => progressCheck.ID === progressCheckID);

        if (!progressCheck) reject("Progress check not found");

        if (!progressCheck!.KeepOpenOnCompletion) reject("Progress check is not set to KeepOpenOnCompletion");

        const url = PF_SERVER_URL + "/portfolio/Mentor/updateSubmissionStatus";
        const response: Response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            token: `${user.token}`,
            contactId: user.userData.contactID,
            menteeContactId: user.userData.contactID,
            pMId: program.ID,
            pCId: progressCheckID,
            operation: "In-Progress",
            role: "Mentee",
          }),
        });

        if (response) {
          const json = await response.json();

          if (json.success) {
            resolve(true);
          } else {
            reject(json.message || json.error);
          }
        } else {
          reject("The server didn't respond");
        }
      }
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      reject("An error has occurred");
    }
  });
}

export async function updateProgressCheckCycle(
  user: IUser,
  programID: string,
  newCycle: number
): Promise<boolean | any> {
  return new Promise(async (resolve, reject) => {
    try {
      const url = PF_SERVER_URL + "/portfolio/Mentor/updateProgramCycle";

      const response: Response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          token: `${user.token}`,
          contactId: user.userData.contactID,
          menteeContactId: user.userData.contactID,
          pMId: programID,
          cycle: `${newCycle}`,
          role: "Mentee",
        }),
      });

      if (response) {
        const json = await response.json();

        if (json.success) {
          resolve(true);
        } else {
          reject(json.message || json.error);
        }
      } else {
        reject("The server didn't respond");
      }
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      reject("An error has occurred");
    }
  });
}
